import React, { useState, useEffect } from "react";
import { addDays } from "date-fns";
import Calendar from "react-calendar";
//import { trackPromise } from "react-promise-tracker";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Modal from "@material-ui/core/Modal";

import { getDateRange, formatTime } from "../../utils/formatTime";
import isMeasurement from "../../utils/isMeasurement";

import "./timeslot.page.scss";

// API
import fetchTimeslotsAsync from "./api/fetchTimeslotsAsync";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  spinnerProgress: {
    position: "absolute",
    top: "60%",
    left: "50%",
    marginTop: -5,
    marginLeft: -5,
  },
}));

// eslint-disable-next-line
Date.prototype.yyyymmdd = function () {
  var mm = this.getMonth() + 1; // getMonth() is zero-based
  var dd = this.getDate();

  return [this.getFullYear(), (mm > 9 ? "" : "0") + mm, (dd > 9 ? "" : "0") + dd].join("");
};

export default function TimeslotPage({ fields, errors, handleChange }) {
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  const [loadingTimeslots, setLoadingTimeslots] = React.useState(false);
  const [reservingTimeslot, setReservingTimeslot] = React.useState(false);

  const [timeslots, setTimeslots] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [date, setDate] = useState(new Date());
  const [minDate, setMinDate] = useState(undefined);
  const [maxDate, setMaxDate] = useState(undefined);

  // modal message displayed when no slot found
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // componentDidMount
  useEffect(() => {
    if (!loadingTimeslots) {
      setLoadingTimeslots(true);

      fetchTimeslotsAsync({
        uuid: fields.uuid,
        dayCount: 30,
        agentCode: fields.service.AgentCode,
        postCode: isMeasurement(fields.service) ? fields.district.Code : fields.store.PostalCode,
        storeNo: isMeasurement(fields.service) ? fields.district.StoreNo : fields.store.StoreNo,
        queueId: fields.store.QueueId,
      }).then((timeslots) => {
        //## response update need to be done
        const firstElement = timeslots.length ? timeslots[0] : undefined;

        firstElement ? setMinDate(new Date(firstElement.actual_date)) : setMinDate(undefined);
        minDate ? setMaxDate(addDays(minDate, 30)) : setMaxDate(undefined);

        // if no timeslots found, show a modal message
        if (timeslots.length === 0) {
          handleOpen();
        }

        setTimeslots(timeslots);
        setLoadingTimeslots(false);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (date) => {
    if (date) {
      const ttts = timeslots.filter((ts) => ts.date === date.yyyymmdd());
      setFiltered(ttts);

      if (isMeasurement(fields.service)) {
        handleChange("timeslot")(ttts[0]);
      }
    }

    setDate(date);
  };

  const onTimeslotChange = (e) => {
    // get object and store it in state
    const ts = timeslots.find((ts) => ts.id === e.target.value);

    setReservingTimeslot(true);
    handleChange("timeslot")(ts);
    setReservingTimeslot(false);
  };

  const showDateRange = (actual_date) => {
    const range = getDateRange(actual_date);

    return (
      <Typography variant="body1" gutterBottom>
        {t("Service appointment") + " " + t("between") + " "}
        {range[0]}
        {" " + t("and") + " "}
        {range[1]}
      </Typography>
    );
  };

  const tileDisabled = ({ activeStartDate, date, view }) => {
    if (timeslots && timeslots.length) {
      if (view === "month") {
        const yyyymmdd = date.yyyymmdd();
        const found = timeslots.find((ts) => ts.date === yyyymmdd);

        return !found; // don't disable
      }

      if (view === "year") {
        const month = date.getMonth();
        const found = timeslots.find((ts) => {
          const tsDate = new Date(ts.actual_date);
          return tsDate.getMonth() === month;
        });

        return !found; // don't disable
      }

      if (view === "decade") {
        const year = date.getYear();
        const found = timeslots.find((ts) => {
          const tsDate = new Date(ts.actual_date);
          return tsDate.getYear() === year;
        });

        return !found; // don't disable
      }

      if (view === "century") {
        const year = date.getYear();
        return year !== 121; // don't disable
      }
    }

    return true;
  };

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        {t("Choose date")}:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Calendar
            calendarType="Arabic"
            // Egypt locale shows the correct gregorian month names in Arabic
            locale={i18n.language === "ar" ? "ar-EG" : "en-US"}
            onChange={onChange}
            value={date}
            minDate={minDate}
            maxDate={maxDate}
            tileDisabled={tileDisabled}
          />
          {loadingTimeslots && <CircularProgress size={24} className={classes.spinnerProgress} />}

          <br />

          <Typography variant="h6" gutterBottom>
            {t("Available timeslots")}:
          </Typography>

          {isMeasurement(fields.service) ? (
            <div>
              {!!fields.timeslot ? (
                showDateRange(fields.timeslot.actual_date)
              ) : (
                <Typography variant="body1" gutterBottom>
                  {t("Please select a date from the above calendar.")}
                </Typography>
              )}
            </div>
          ) : (
            <div>
              <Typography variant="body1" gutterBottom>
                {t("Please be available 10 minutes before the selected time.")}
              </Typography>

              <FormControl fullWidth error={!!errors["timeslot"]}>
                <InputLabel id="timeslot-select-label">{t("Timeslot")}</InputLabel>
                <Select
                  labelId="timeslot-select-label"
                  label={t("Timeslot")}
                  id="timeslot-select"
                  value={fields.timeslot ? fields.timeslot.id : ""}
                  onChange={onTimeslotChange}
                >
                  {filtered &&
                    filtered.map((timeslot) => {
                      return (
                        <MenuItem key={timeslot.id} value={timeslot.id}>
                          {formatTime(timeslot.start_time, i18n.language)}
                        </MenuItem>
                      );
                    })}
                </Select>
                {reservingTimeslot && <CircularProgress size={24} className={classes.spinnerProgress} />}
                <FormHelperText>{errors["timeslot"]}</FormHelperText>
              </FormControl>
            </div>
          )}
        </Grid>
      </Grid>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <div className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            {t("No time slot available")}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t("Sorry, No time slot available for the selected service. Please check again later.")}
          </Typography>
        </div>
      </Modal>
    </div>
  );
}
